"use client";

import * as api from "@/src/api/sdk.gen";
import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { Link, useRouter } from "@/src/lib/next-compat";
import {  useNavigate  } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { invalidateCourseCore } from "@/lib/student/course-core-query";
import { courseResourcesQueryKey, fetchCourseResources } from "@/lib/student/course-resources-query";
import type { CachedCourseData } from "@/lib/contexts/CourseNavigationContext";
import { ChevronRight, CheckCircle, Lock, PlayCircle, Menu, ArrowLeft, ArrowRight, BookOpen, Download, Loader2, Link as LinkIcon, FileText, AlignLeft, Eye, ExternalLink, RefreshCw, RotateCcw, Languages, Sparkles, Maximize2, Minimize2, PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { useAuth } from "@clerk/clerk-react";
import { useStudentSidebar } from "@/src/context/StudentSidebarContext";
import ResourceDrawer from "@/components/student/ResourceDrawer";
import type { Resource } from "@/components/admin/ResourceList";
import ResourceViewerModal from "@/components/admin/ResourceViewerModal";
import { exportFullLessonToPdf, exportLessonRootToPdf, lessonPdfFilename } from "@/lib/lesson-pdf-export";
import Markdown from "@/components/Markdown";
import ProactiveLessonContent, { isProactiveStructuredData } from "@/components/student/ProactiveLessonContent";
import ProfessionalProactiveLessonContent, { isProfessionalProactiveStructuredData } from "@/components/student/ProfessionalProactiveLessonContent";
import LegacySectionsLessonContent, { isLegacySectionsData } from "@/components/student/LegacySectionsLessonContent";
import RawJsonToggle from "@/components/student/RawJsonToggle";
import * as Tooltip from '@radix-ui/react-tooltip';
import { useProactiveLessonPersonalization } from "@/hooks/useProactiveStatus";
import {
    lessonContentDirection,
    normalizeLessonLanguage,
    type LessonLanguage,
} from "@/lib/lesson-language";
import { resolveBlocksLesson } from "@/lib/ile/detect";
import { isInteractiveBlock } from "@/lib/ile/block-meta";
import { clearIleSessionProgress } from "@/lib/ile/session-storage";
import type { LessonContent } from "@/lib/lesson-content/schema";
import InteractiveLessonRunner from "@/components/ile/InteractiveLessonRunner";
import { LessonProgressProvider } from "@/components/ile/LessonProgressProvider";
import { LessonLanguageProvider } from "@/components/ile/LessonLanguageProvider";
import LessonFooterNav from "@/components/ile/LessonFooterNav";

/** Best-effort text extraction for the generic `{sections: [{heading, content}]}` fallback shape. */
function extractFallbackSectionText(structuredContent: unknown): string {
    if (typeof structuredContent !== "object" || structuredContent === null) return "";
    const sections = (structuredContent as Record<string, unknown>).sections;
    if (!Array.isArray(sections)) return "";
    return sections
        .map((s) => (s && typeof s === "object" ? (s as Record<string, unknown>).content : null))
        .filter((c): c is string => typeof c === "string")
        .join("\n\n");
}

/** Renders blocks, proactive, or markdown lesson content. */
function LessonContentHandler({
    lesson,
    blocksContent,
    positionLabel,
    lessonCompletedInCourse = false,
}: {
    lesson: LessonData;
    blocksContent: LessonContent | null;
    positionLabel?: string;
    lessonCompletedInCourse?: boolean;
}) {
    if (blocksContent) {
        return (
            <InteractiveLessonRunner
                lessonTitle={lesson.title}
                moduleTitle={lesson.moduleTitle}
                positionLabel={positionLabel}
            />
        );
    }
    if (isProfessionalProactiveStructuredData(lesson.structuredContent)) {
        return (
            <ProfessionalProactiveLessonContent
                data={lesson.structuredContent}
                lessonCompletedInCourse={lessonCompletedInCourse}
                workplaceScenarioPending={lesson.blockPersonalizationPending}
            />
        );
    }
    if (isProactiveStructuredData(lesson.structuredContent)) {
        return <ProactiveLessonContent data={lesson.structuredContent} lessonCompletedInCourse={lessonCompletedInCourse} />;
    }
    if (isLegacySectionsData(lesson.structuredContent)) {
        return <LegacySectionsLessonContent data={lesson.structuredContent} />;
    }
    // No content at all (still generating) — the "preparing your lesson" banner
    // above already communicates this; don't also show a format-issue warning.
    if (lesson.structuredContent == null) {
        return null;
    }
    return (
        <>
            <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                Content format issue. Showing raw content.
            </div>
            <Markdown content={extractFallbackSectionText(lesson.structuredContent)} />
        </>
    );
}

// Types matching what we return from getStudentLesson
type LessonData = {
    id: string;
    title: string;
    structuredContent?: unknown;
    moduleId: number;
    moduleTitle: string;
    canonicalLessonId: number;
    language: LessonLanguage;
    blockPersonalizationPending?: boolean;
    availableLanguages?: string[];
    languageVariants?: Record<string, string>;
};

type CourseData = {
    id: string;
    uuid?: string | null;
    title: string;
    progress: number;
    level?: string;
    organizationName?: string | null;
    userDomain?: string | null;
    available_languages?: string[];
    modules: {
        id: number;
        title: string;
        isLocked: boolean;
        hasModuleQuiz?: boolean;
        moduleQuizPassed?: boolean;
        moduleQuizAvailable?: boolean;
        earnedPoints?: number;
        level: {
            id: number;
            name: string;
            level_number: number;
        } | null;
        lessons: {
            id: string;
            /** Canonical `lessons.id`; outline `id` is proactive UUID when present. */
            canonicalLessonId?: number;
            title: string;
            completed: boolean;
            type?: string;
            hasContent?: boolean;
            isLocked?: boolean;
            duration?: string;
            earnedPoints?: number;
        }[];
    }[];
};


export default function LessonPlayer({
    lesson,
    course,
    outlineActionsDisabled = false,
    lessonLoading = false,
}: {
    lesson: LessonData;
    course: CourseData;
    /** True until the real course outline is available (placeholder shell); blocks quiz/next/finish. */
    outlineActionsDisabled?: boolean;
    /** True while lesson payload is still fetching; keep shell visible and show loading content. */
    lessonLoading?: boolean;
}) {
    const categoryLabel = course?.organizationName ? `${course.organizationName} Masterclass` : "Masterclass";
    const industryDisplay = course?.userDomain ?? "—";
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const courseParam = (course as { uuid?: string; id: number | string })?.uuid || String(course.id);

    // Patch the cached outline in place with the server-computed total instead of
    // refetching the whole /core tree on every section-complete (avoids a refetch
    // per section). Predicate-matches any course-core cache and only touches the
    // current lesson, so it's a safe no-op when nothing matches. Stable so the
    // lesson-progress effects that depend on it don't re-fire each render.
    const currentCanonicalId = lesson.canonicalLessonId;
    const refreshOutlinePoints = useCallback(
        (lessonEarnedTotal: number) => {
            queryClient.setQueriesData<CachedCourseData>(
                { predicate: (q) => Array.isArray(q.queryKey) && q.queryKey[0] === "course-core" },
                (old) => {
                    if (!old?.modules) return old;
                    let changed = false;
                    const modules = old.modules.map((m) => {
                        let moduleChanged = false;
                        const lessons = m.lessons.map((l) => {
                            if (l.canonicalLessonId === currentCanonicalId && l.earnedPoints !== lessonEarnedTotal) {
                                moduleChanged = true;
                                return { ...l, earnedPoints: lessonEarnedTotal };
                            }
                            return l;
                        });
                        if (!moduleChanged) return m;
                        changed = true;
                        return { ...m, lessons, earnedPoints: lessons.reduce((a, l) => a + (l.earnedPoints ?? 0), 0) };
                    });
                    return changed ? { ...old, modules } : old;
                },
            );
        },
        [queryClient, currentCanonicalId],
    );

    // Total earned points across all modules/lessons in the course
    const totalEarnedPoints = useMemo(() => {
        if (!course?.modules) return 0;
        return course.modules.reduce((acc, m) => {
            const modPts = m.earnedPoints ?? m.lessons.reduce((lAcc, l) => lAcc + (l.earnedPoints ?? 0), 0);
            return acc + modPts;
        }, 0);
    }, [course?.modules]);
    const lessonPdfRootRef = useRef<HTMLDivElement>(null);
    const pdfExportLockRef = useRef(false);
    const [pdfExporting, setPdfExporting] = useState(false);
    const [isCompleting, setIsCompleting] = useState(false);
    const [completeError, setCompleteError] = useState<string | null>(null);
    // Controls the desktop course outline sidebar (hidden on mobile)
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);
    const {
        isDesktopCollapsed,
        setIsDesktopCollapsed,
        toggleDesktopSidebar,
        setIsMobileOpen,
    } = useStudentSidebar();

    // Restore desktop sidebar when leaving the lesson player
    useEffect(() => {
        return () => {
            setIsDesktopCollapsed(false);
        };
    }, [setIsDesktopCollapsed]);

    const isFullyExpanded = !isSidebarOpen && isDesktopCollapsed;

    const handleToggleExpand = () => {
        if (isFullyExpanded) {
            // Restore standard view: show outline and restore left navbar
            setIsSidebarOpen(true);
            setIsDesktopCollapsed(false);
        } else {
            // Fluid Fullscreen: collapse both outline and left navbar
            setIsSidebarOpen(false);
            setIsDesktopCollapsed(true);
        }
    };
    // Outline modules are collapsed by default; only the module containing the
    // open lesson starts expanded.
    const [expandedModules, setExpandedModules] = useState<number[]>(() =>
        lesson.moduleId ? [lesson.moduleId] : [],
    );
    const toggleModule = (moduleId: number) =>
        setExpandedModules((prev) =>
            prev.includes(moduleId) ? prev.filter((id) => id !== moduleId) : [...prev, moduleId],
        );
    // Lesson resources — reads the same whole-course cache entry as
    // CourseDetail/ResourceDrawer instead of issuing its own per-lesson fetch
    // (see F7 in docs/api_performance_audit.md), filtered client-side.
    const [isResourceDrawerOpen, setIsResourceDrawerOpen] = useState(false);
    const [viewingResource, setViewingResource] = useState<Resource | null>(null);
    const lessonLanguage = normalizeLessonLanguage(lesson.language);
    const contentDirection = lessonContentDirection(lessonLanguage);

    const [switchingLanguage, setSwitchingLanguage] = useState(false);

    const availableLanguages = useMemo(() => {
        const list: string[] = [];
        if (lesson.availableLanguages && lesson.availableLanguages.length > 0) {
            list.push(...lesson.availableLanguages);
        }
        if (lesson.languageVariants) {
            list.push(...Object.keys(lesson.languageVariants));
        }
        if (course.available_languages && course.available_languages.length > 0) {
            list.push(...course.available_languages);
        }
        if (lessonLanguage === "Arabic") {
            list.push("Arabic", "English");
        }
        const unique = Array.from(
            new Set(list.map((l) => (l.toLowerCase() === "arabic" || l === "ar" ? "Arabic" : "English"))),
        );
        return unique.length > 0 ? unique : ["English"];
    }, [lesson.availableLanguages, lesson.languageVariants, course.available_languages, lessonLanguage]);

    // Always allow language switcher between English and Arabic
    const canSwitchLanguage = true;
    const targetLanguage: "English" | "Arabic" = lessonLanguage === "Arabic" ? "English" : "Arabic";

    const handleSwitchLanguage = async (targetLang: "English" | "Arabic" = targetLanguage) => {
        if (targetLang === lessonLanguage || switchingLanguage) return;
        setSwitchingLanguage(true);
        try {
            let targetLessonId = lesson.languageVariants?.[targetLang];
            if (!targetLessonId) {
                try {
                    const res = await api.getLessonPayloadApiV1CoursesCourseIdLessonsLessonIdPayloadGet({
                        path: { course_id: courseParam, lesson_id: String(lesson.canonicalLessonId || lesson.id) },
                        query: { language: targetLang },
                    });
                    const payload = res.data as { kind: string; lesson?: { id: string } };
                    if (payload?.lesson?.id) {
                        targetLessonId = payload.lesson.id;
                    }
                } catch (err) {
                    console.warn("Could not pre-fetch target language payload:", err);
                }
            }

            try {
                await api.updateUserSettingsApiV1UsersSettingsPatch({
                    body: {
                        course_id: parseInt(courseParam, 10) || undefined,
                        language: targetLang,
                    },
                });
            } catch {
                // Non-critical preference save
            }

            await queryClient.invalidateQueries({ queryKey: ["lesson-payload", courseParam] });
            if (targetLessonId && targetLessonId !== lesson.id) {
                navigate(`/courses/${courseParam}/lessons/${targetLessonId}`);
            } else {
                navigate(`/courses/${courseParam}/lessons/${lesson.canonicalLessonId || lesson.id}`);
            }
        } catch (err) {
            console.error("Failed switching language:", err);
        } finally {
            setSwitchingLanguage(false);
        }
    };

    const courseResourcesQuery = useQuery({
        queryKey: courseResourcesQueryKey(courseParam),
        queryFn: () => fetchCourseResources(courseParam),
        staleTime: 60_000,
        enabled: !!lesson.canonicalLessonId && lesson.canonicalLessonId > 0,
    });
    const lessonResources = useMemo(
        () =>
            (courseResourcesQuery.data ?? []).filter(
                (r) => r.lesson_id === lesson.canonicalLessonId,
            ) as Resource[],
        [courseResourcesQuery.data, lesson.canonicalLessonId],
    );

    const blocksContent = useMemo(
        () => resolveBlocksLesson(lesson.structuredContent),
        [lesson.structuredContent],
    );

    // Flatten lessons to find prev/next
    const allLessons = useMemo(() => {
        return course.modules.flatMap(m => m.lessons.map(l => ({
            ...l,
            moduleId: m.id,
            moduleTitle: m.title
        })));
    }, [course]);

    const currentIndex = allLessons.findIndex(
        (l) =>
            l.id === lesson.id ||
            (typeof l.canonicalLessonId === "number" &&
                typeof lesson.canonicalLessonId === "number" &&
                l.canonicalLessonId === lesson.canonicalLessonId &&
                lesson.canonicalLessonId > 0) ||
            (typeof l.id === "string" && typeof lesson.id === "string" && l.id.toLowerCase() === lesson.id.toLowerCase()) ||
            (l.title && lesson.title && l.title.trim().toLowerCase() === lesson.title.trim().toLowerCase()),
    );
    const currentLessonData = allLessons[currentIndex];
    const [completedLocally, setCompletedLocally] = useState(false);
    useEffect(() => {
        setCompletedLocally(false);
    }, [lesson.id]);
    const isLessonCompleted = Boolean(currentLessonData?.completed || completedLocally);

    const handleSavePdf = useCallback(async () => {
        if (!isLessonCompleted) return;
        if (pdfExportLockRef.current) return;
        pdfExportLockRef.current = true;
        setPdfExporting(true);
        try {
            const name = lessonPdfFilename(course.title, lesson.title);
            await exportFullLessonToPdf({
                courseTitle: course.title,
                lessonTitle: lesson.title,
                moduleTitle: lesson.moduleTitle,
                industryDomain: course.userDomain ?? course.organizationName ?? null,
                language: lesson.language,
                blocksContent,
                structuredContent: lesson.structuredContent,
                fallbackRoot: lessonPdfRootRef.current,
            }, name);
        } catch (e) {
            console.error("[LessonPlayer] PDF export failed", e);
            alert("Could not create the PDF. Try again in a moment.");
        } finally {
            pdfExportLockRef.current = false;
            setPdfExporting(false);
        }
    }, [isLessonCompleted, course.title, course.userDomain, course.organizationName, lesson.title, lesson.moduleTitle, lesson.language, lesson.structuredContent, blocksContent]);

    const moduleIndex = course.modules.findIndex(m => m.id === lesson.moduleId);
    const moduleNumber = moduleIndex !== -1 ? moduleIndex + 1 : "";
    const lessonBreadcrumb = moduleNumber ? `Module ${moduleNumber}: ${lesson.title}` : lesson.title;
    const currentModule = moduleIndex >= 0 ? course.modules[moduleIndex] : null;
    const lessonNumberInModule = currentModule
        ? (currentModule.lessons.findIndex(l => l.id === lesson.id) + 1) || 1
        : 1;
    const tierLabel = course.level && course.level !== "Not determined yet" ? course.level : null;

    const prevLesson = currentIndex > 0 ? allLessons[currentIndex - 1] : null;
    const nextLesson = currentIndex < allLessons.length - 1 ? allLessons[currentIndex + 1] : null;

    const isLastLessonInModule =
        !nextLesson || nextLesson.moduleId !== lesson.moduleId;
    const showModuleQuizRedirect = Boolean(
        isLastLessonInModule &&
            currentModule?.hasModuleQuiz &&
            !currentModule?.moduleQuizPassed,
    );
    const showModuleQuizPassed = Boolean(
        currentModule?.hasModuleQuiz && currentModule?.moduleQuizPassed,
    );
    const nextLabel = showModuleQuizRedirect
        ? "Take Module Quiz"
        : nextLesson
          ? "Next"
          : "Finish Course";

    const isRtlLanguage = contentDirection === "rtl";

    const auth = useAuth();
    const hasActivities = useMemo(() => {
        return Boolean(blocksContent?.sections?.some((s) => s.blocks.some((b) => isInteractiveBlock(b.type))));
    }, [blocksContent]);

    const [retryCount, setRetryCount] = useState(0);

    useEffect(() => {
        setRetryCount(0);
    }, [lesson.id]);

    const handleRetryActivities = useCallback(() => {
        clearIleSessionProgress(String(lesson.id), auth?.userId ?? null);
        setRetryCount((prev) => prev + 1);
        if (typeof window !== "undefined") {
            window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
        }
        const scrollContainer = lessonPdfRootRef.current?.querySelector("[data-lesson-pdf-scroll]");
        if (scrollContainer) {
            scrollContainer.scrollTo({ top: 0, left: 0, behavior: "smooth" });
        }
    }, [lesson.id, auth?.userId]);

    const isBlocksLesson = blocksContent !== null;
    // Block lessons run through the proactive pipeline (category-keyed) like prose
    // lessons: readiness comes from the category proactive row, so when it's not ready
    // we trigger generation + show the spinner, uniformly (no lesson-type branching).
    const isProactiveReady = currentLessonData?.hasContent !== false;

    const router = useRouter();
    const {
        phase: personalizePhase,
        error: personalizeError,
        lastTaskId,
        statusRefreshing,
        bannerHeadline,
        scheduleRetry,
        refreshStatus,
    } = useProactiveLessonPersonalization({
        canonicalLessonId: lesson.canonicalLessonId,
        contentReady: isProactiveReady,
        router,
    });

    // Always scroll to top when entering a lesson or switching between lessons
    useEffect(() => {
        if (typeof window !== "undefined") {
            window.scrollTo({ top: 0, left: 0, behavior: "auto" });
        }
        setCompleteError(null);
    }, [lesson.id]);

    // If specialized (proactive) content is not yet generated for this lesson,
    // trigger background generation once when the user opens the lesson.
    // Must be called unconditionally (before any early return) to satisfy rules of hooks.
    useEffect(() => {
        if (isProactiveReady) return;
        if (!lesson.canonicalLessonId || lesson.canonicalLessonId < 1) return;
        // If the lesson is open in the player, we should usually be authorized.
        // Still, avoid spamming the endpoint if the session is missing.
        // (Auth failures are handled server-side; this is just a cheap guard.)
        if (typeof document !== "undefined" && document.visibilityState === "hidden") return;

        let cancelled = false;

        (async () => {
            try {
                await api.triggerProactiveGenerationApiV1LessonsLessonIdProactiveGenerationPost({
                    path: { lesson_id: lesson.canonicalLessonId },
                });
            } catch (error) {
                if (!cancelled) {
                    console.error("[LessonPlayer] Failed to trigger proactive generation", error);
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [isProactiveReady, lesson.canonicalLessonId]);
    // Safety check - if lesson not found in course list?
    if (currentIndex === -1 && !lessonLoading) {
        return <div className="p-8">Lesson context not found</div>;
    }

    const handleNext = async () => {
        if (outlineActionsDisabled || isCompleting) {
            return;
        }

        const currentMod = course.modules.find(m => m.id === lesson.moduleId);
        const nextMod = nextLesson ? course.modules.find(m => m.id === nextLesson.moduleId) : null;
        const prevModForNext = nextMod && course.modules.findIndex(m => m.id === nextMod.id) > 0
            ? course.modules[course.modules.findIndex(m => m.id === nextMod.id) - 1]
            : null;

        setIsCompleting(true);
        try {
            await api.completeLessonApiV1CoursesCourseIdLessonsLessonIdCompletePost({
                path: { course_id: courseParam, lesson_id: String(lesson.id) },
            });
            void invalidateCourseCore(queryClient, courseParam);
            setCompletedLocally(true);
            setCompleteError(null);
        } catch (error) {
            console.error("[LessonPlayer] Failed to complete lesson", error);
            setCompleteError("We couldn't save your progress for this lesson. Please try again.");
            setIsCompleting(false);
            return;
        }
        setIsCompleting(false);

        if (showModuleQuizRedirect) {
            navigate(`/courses/${courseParam}/module-quiz/${lesson.moduleId}`);
            return;
        }

        if (nextLesson) {
            const crossesModule = nextLesson.moduleId !== lesson.moduleId;
            if (
                crossesModule &&
                prevModForNext?.hasModuleQuiz &&
                !prevModForNext.moduleQuizPassed
            ) {
                navigate(`/courses/${courseParam}`);
                return;
            }
            navigate(`/courses/${courseParam}/lessons/${nextLesson.id}`);
        } else if (
            currentMod?.hasModuleQuiz &&
            currentMod?.moduleQuizAvailable
        ) {
            navigate(`/courses/${courseParam}`);
        } else {
            navigate(`/courses/${courseParam}`);
        }
    };

    const handleMenuClick = () => {
        if (typeof window !== "undefined" && window.innerWidth < 1024) {
            setIsMobileOpen((prev) => !prev);
        } else {
            toggleDesktopSidebar();
        }
    };


    const mainContent = (
                <main className="flex-1 flex flex-col h-full overflow-hidden relative w-full min-w-0 font-montserrat">
                    {/* PDF capture: top bar + full lesson scroll (excludes bottom nav and course sidebar) */}
                    <div
                        ref={lessonPdfRootRef}
                        className="flex flex-col flex-1 min-h-0 overflow-hidden bg-white"
                    >
                        {/* Navigation */}
                        <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-200 shrink-0">
                            <div className={`mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3 transition-all duration-300 ${isSidebarOpen ? "max-w-5xl" : "max-w-7xl"}`}>
                                <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                                    <button
                                        type="button"
                                        className="p-1.5 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors flex-shrink-0 cursor-pointer"
                                        onClick={handleMenuClick}
                                        aria-label={isDesktopCollapsed ? "Show navigation sidebar" : "Hide navigation sidebar"}
                                        title={isDesktopCollapsed ? "Show navigation sidebar" : "Hide navigation sidebar"}
                                    >
                                        <div className="hidden lg:block">
                                            {isDesktopCollapsed ? (
                                                <PanelLeftOpen className="w-5 h-5 text-indigo-600" />
                                            ) : (
                                                <PanelLeftClose className="w-5 h-5 text-slate-600" />
                                            )}
                                        </div>
                                        <div className="lg:hidden">
                                            <Menu className="w-5 h-5" />
                                        </div>
                                    </button>
                                    <Link
                                        to={`/courses/${course.id}`}
                                        className="w-9 h-9 rounded-lg flex items-center justify-center text-white flex-shrink-0 bg-[#0050EF] hover:bg-[#0040c4] transition-colors"
                                        aria-label="Back to course overview"
                                    >
                                        <ArrowLeft className="w-5 h-5" />
                                    </Link>
                                    <div className="min-w-0">
                                        <p className="text-[9px] font-black text-indigo-600 uppercase tracking-[0.3em] leading-tight">{categoryLabel}</p>
                                        <h2 className="text-base font-bold text-slate-800 flex items-center gap-2 min-w-0">
                                            <span className="truncate">{lesson.title}</span>
                                            {canSwitchLanguage ? (
                                                <button
                                                    type="button"
                                                    onClick={() => handleSwitchLanguage(targetLanguage)}
                                                    disabled={switchingLanguage}
                                                    className="shrink-0 inline-flex items-center gap-1 rounded-md bg-slate-100 hover:bg-slate-200 px-2 py-0.5 text-[10px] font-semibold text-slate-700 transition-colors cursor-pointer border border-slate-200 active:scale-95"
                                                    title={`Currently in ${lessonLanguage}. Click to switch to ${targetLanguage}`}
                                                    aria-label={`Switch lesson language to ${targetLanguage}`}
                                                >
                                                    {switchingLanguage ? (
                                                        <Loader2 className="w-3 h-3 animate-spin text-indigo-600" aria-hidden />
                                                    ) : (
                                                        <Languages className="w-3 h-3 text-indigo-600" aria-hidden />
                                                    )}
                                                    <span>{lessonLanguage}</span>
                                                </button>
                                            ) : (
                                                <span
                                                    className="shrink-0 rounded-md bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600"
                                                    title="Lesson language"
                                                >
                                                    {lessonLanguage}
                                                </span>
                                            )}
                                        </h2>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
                                    {totalEarnedPoints > 0 && (
                                        <div className="hidden sm:inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-amber-50 text-amber-800 border border-amber-200 text-xs font-bold shrink-0">
                                            <Sparkles className="w-3.5 h-3.5 text-amber-500" />
                                            <span>{totalEarnedPoints} pts</span>
                                        </div>
                                    )}
                                    <div className="hidden md:flex items-center gap-2 text-xs font-semibold text-slate-600 shrink-0">
                                        <div className="w-16 lg:w-20 bg-slate-200 rounded-full h-1.5 overflow-hidden">
                                            <div
                                                className="bg-indigo-600 h-full rounded-full transition-all duration-300"
                                                style={{ width: `${course.progress}%` }}
                                            />
                                        </div>
                                        <span>{course.progress}%</span>
                                    </div>
                                    {isLessonCompleted && hasActivities && (
                                        <button
                                            type="button"
                                            onClick={handleRetryActivities}
                                            className="inline-flex items-center gap-1.5 px-2.5 py-2 sm:px-3 rounded-lg border border-slate-200 bg-white text-slate-700 text-xs font-semibold hover:bg-slate-50 transition-colors cursor-pointer"
                                            title="Retry lesson activities"
                                            aria-label="Retry lesson activities"
                                        >
                                            <RotateCcw className="w-4 h-4 text-indigo-600" aria-hidden />
                                            <span className="hidden sm:inline">Retry</span>
                                        </button>
                                    )}
                                    {lessonResources.length > 0 && (
                                        <button
                                            type="button"
                                            onClick={() => setIsResourceDrawerOpen(true)}
                                            className="inline-flex items-center gap-1.5 px-2.5 py-2 sm:px-3 rounded-lg border border-slate-200 bg-white text-slate-700 text-xs font-semibold hover:bg-slate-50 transition-colors"
                                            title="Lesson resources"
                                        >
                                            <BookOpen className="w-4 h-4 text-indigo-600" aria-hidden />
                                            <span className="hidden sm:inline">{lessonResources.length}</span>
                                        </button>
                                    )}
                                    {isLessonCompleted && (
                                        <button
                                            type="button"
                                            onClick={handleSavePdf}
                                            disabled={pdfExporting}
                                            className="inline-flex items-center gap-1.5 px-2.5 py-2 sm:px-3 rounded-lg border border-slate-200 bg-white text-slate-700 text-xs font-semibold hover:bg-slate-50 disabled:opacity-50 disabled:pointer-events-none transition-colors cursor-pointer"
                                            title="Save this lesson as a PDF"
                                        >
                                            {pdfExporting ? (
                                                <Loader2 className="w-4 h-4 animate-spin" aria-hidden />
                                            ) : (
                                                <Download className="w-4 h-4" aria-hidden />
                                            )}
                                            <span className="sm:hidden text-[11px] font-bold">PDF</span>
                                        </button>
                                    )}
                                    {canSwitchLanguage && (
                                        <button
                                            type="button"
                                            onClick={() => handleSwitchLanguage(targetLanguage)}
                                            disabled={switchingLanguage}
                                            className="inline-flex items-center gap-1.5 px-2.5 py-2 sm:px-3 rounded-lg border border-slate-200 bg-white text-slate-700 text-xs font-semibold hover:bg-slate-50 hover:border-slate-300 transition-colors shadow-sm disabled:opacity-60 cursor-pointer active:scale-95"
                                            title={targetLanguage === "English" ? "Switch to English" : "التبديل إلى العربية (Switch to Arabic)"}
                                            aria-label="Switch lesson language"
                                        >
                                            {switchingLanguage ? (
                                                <Loader2 className="w-4 h-4 animate-spin text-indigo-600" aria-hidden />
                                            ) : (
                                                <Languages className="w-4 h-4 text-indigo-600" aria-hidden />
                                            )}
                                            <span className="font-semibold text-xs">
                                                {targetLanguage === "English" ? "English" : "العربية"}
                                            </span>
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        onClick={handleToggleExpand}
                                        className={`inline-flex items-center gap-1.5 px-2.5 py-2 sm:px-3 rounded-lg border text-xs font-semibold transition-colors cursor-pointer ${
                                            isFullyExpanded
                                                ? "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                                                : "border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100"
                                        }`}
                                        title={isFullyExpanded ? "Restore standard view with navigation & outline" : "Fluid Fullscreen (hide sidebars)"}
                                        aria-label="Toggle fluid fullscreen view"
                                    >
                                        {isFullyExpanded ? (
                                            <Minimize2 className="w-3.5 h-3.5 text-slate-600" />
                                        ) : (
                                            <Maximize2 className="w-3.5 h-3.5 text-indigo-600" />
                                        )}
                                        <span className="hidden md:inline">{isFullyExpanded ? "Standard" : "Expand"}</span>
                                    </button>
                                    <div className="hidden sm:flex flex-col items-end">
                                        <span className="text-[9px] text-slate-400 font-black uppercase tracking-widest">Industry focus</span>
                                        <span className="text-xs font-bold text-slate-600">{industryDisplay}</span>
                                    </div>
                                </div>
                            </div>
                        </nav>

                        {/* Scrollable lesson content */}
                        <div
                            data-lesson-pdf-scroll
                            className={`flex-1 overflow-y-auto px-4 sm:px-6 lg:px-10 pt-6 pb-4 w-full transition-all duration-300 min-h-0 mx-auto ${
                                isSidebarOpen ? "max-w-5xl" : "max-w-7xl"
                            }`}
                        >
                            <div className="max-w-none mx-auto w-full">
                                <article
                                    className="learning-content max-w-none"
                                    dir={contentDirection}
                                    lang={lessonLanguage === "Arabic" ? "ar" : "en"}
                                >
                                    {!isProactiveReady && (
                                        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 space-y-2">
                                            {bannerHeadline && (
                                                <p className="font-semibold text-amber-900">
                                                    {bannerHeadline}
                                                </p>
                                            )}
                                            <p>
                                                We&apos;re preparing your specialized lesson content. You can read the
                                                base lesson below while your personalized version is generated in the
                                                background.
                                            </p>
                                            {personalizePhase === "polling_timeout" && (
                                                <div className="space-y-2 pt-1">
                                                    <p className="text-amber-900 font-medium">
                                                        Personalization is taking longer than usual. Your lesson may
                                                        still be generating—use Refresh to check, or Retry to queue
                                                        personalization again.
                                                    </p>
                                                    <div className="flex flex-col sm:flex-row flex-wrap gap-2">
                                                        <button
                                                            type="button"
                                                            onClick={() => void refreshStatus()}
                                                            disabled={statusRefreshing}
                                                            className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md bg-[#0050EF] text-white text-xs font-bold hover:bg-[#0040c4] disabled:opacity-50 disabled:pointer-events-none"
                                                        >
                                                            {statusRefreshing ? (
                                                                <Loader2
                                                                    className="w-3.5 h-3.5 animate-spin"
                                                                    aria-hidden
                                                                />
                                                            ) : (
                                                                <RefreshCw className="w-3.5 h-3.5" aria-hidden />
                                                            )}
                                                            Refresh
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={scheduleRetry}
                                                            className="px-3 py-1.5 rounded-md border border-amber-800/30 bg-white text-amber-950 text-xs font-bold hover:bg-amber-100/80"
                                                        >
                                                            Retry
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                            {personalizePhase === "error" && personalizeError && (
                                                <div className="flex flex-col sm:flex-row sm:items-center gap-2 pt-1">
                                                    <p className="text-red-700 font-medium">{personalizeError}</p>
                                                    <button
                                                        type="button"
                                                        onClick={scheduleRetry}
                                                        className="shrink-0 text-left sm:text-center px-3 py-1.5 rounded-md bg-amber-900 text-white text-xs font-bold hover:bg-amber-950"
                                                    >
                                                        Retry
                                                    </button>
                                                </div>
                                            )}
                                            {lastTaskId &&
                                                (personalizePhase === "personalizing" ||
                                                    personalizePhase === "polling" ||
                                                    personalizePhase === "polling_timeout") && (
                                                    <p className="text-[11px] text-amber-700/90 font-mono break-all">
                                                        Task ref: {lastTaskId}
                                                    </p>
                                                )}
                                        </div>
                                    )}
                                    {/* <RawJsonToggle data={lesson.structuredContent} /> */}
                                    {!lessonLoading && (
                                        <LessonContentHandler
                                            lesson={lesson}
                                            blocksContent={blocksContent}
                                            positionLabel={
                                                moduleNumber
                                                    ? `Module ${moduleNumber} · Lesson ${lessonNumberInModule}`
                                                    : undefined
                                            }
                                            lessonCompletedInCourse={Boolean(currentLessonData?.completed)}
                                        />
                                    )}
                                </article>
                            </div>
                        </div>
                    </div>

                    {completeError && (
                        <div className="flex items-center justify-between gap-3 px-4 py-2 bg-red-50 border-t border-red-200">
                            <p className="text-sm text-red-700" role="alert">{completeError}</p>
                            <button
                                type="button"
                                onClick={() => void handleNext()}
                                disabled={isCompleting}
                                className="shrink-0 px-3 py-1.5 rounded-md bg-red-700 text-white text-xs font-bold hover:bg-red-800 disabled:opacity-60"
                            >
                                {isCompleting ? "Retrying…" : "Retry"}
                            </button>
                        </div>
                    )}

                    <LessonFooterNav
                        isBlocksLesson={isBlocksLesson}
                        hasPrev={Boolean(prevLesson)}
                        onPrevious={() =>
                            prevLesson &&
                            navigate(`/courses/${course.id}/lessons/${prevLesson.id}`)
                        }
                        nextLabel={isCompleting ? "Saving…" : nextLabel}
                        onNext={() => void handleNext()}
                        nextDisabled={outlineActionsDisabled || isCompleting}
                        nextTitle={
                            outlineActionsDisabled
                                ? "Course outline is still loading. Try again in a moment."
                                : undefined
                        }
                        showQuizPassed={showModuleQuizPassed && isLastLessonInModule}
                        quizPassedLabel="Module Quiz Passed"
                    />
                </main>
    );

    return (
        <Tooltip.Provider>
            <div className="flex h-screen overflow-hidden bg-white relative">
                <LessonLanguageProvider language={lessonLanguage}>
                    {blocksContent ? (
                        <LessonProgressProvider
                            key={`${lesson.canonicalLessonId || lesson.id}-${Boolean(currentLessonData?.completed)}-${retryCount}`}
                            content={blocksContent}
                            lessonId={String(lesson.canonicalLessonId || lesson.id)}
                            lessonCompletedInCourse={retryCount > 0 ? false : Boolean(currentLessonData?.completed)}
                            onPointsRecorded={refreshOutlinePoints}
                        >
                            {mainContent}
                        </LessonProgressProvider>
                    ) : (
                        mainContent
                    )}
                </LessonLanguageProvider>

                {/* Course Outline Sidebar (desktop) */}
                <aside
                    className={`
                        hidden lg:flex bg-gray-50 border-l border-gray-200 flex-col
                        sticky top-0 h-screen z-40
                        transition-all duration-300 ease-in-out shrink-0 overflow-hidden
                        ${isSidebarOpen ? 'w-80 opacity-100' : 'w-0 opacity-0 border-l-0'}
                    `}
                >
                    <div className="p-3 border-b border-gray-200 flex items-center justify-between min-w-[20rem]">
                        <div className="flex-1 min-w-0">
                            <Link to={`/courses/${courseParam}`} className="text-xs text-gray-500 hover:text-indigo-600 mb-1.5 block flex items-center gap-1">
                                <ArrowLeft className="w-3 h-3" /> Back to Course Overview
                            </Link>
                            <div className="flex items-center justify-between gap-2">
                                <h3 className="font-bold text-gray-900 leading-tight truncate min-w-0">{course.title}</h3>
                                {course.level && course.level !== "Not determined yet" && (
                                    <span className="text-[10px] font-bold text-indigo-600 uppercase tracking-widest bg-indigo-50 px-1.5 py-0.5 rounded border border-indigo-100 flex-shrink-0">
                                        {course.level}
                                    </span>
                                )}
                            </div>
                        </div>
                        <button
                            onClick={() => setIsSidebarOpen(false)}
                            className="ml-2 p-2 text-gray-400 hover:text-gray-900 transition-colors"
                            aria-label="Close course outline"
                            title="Close outline"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                    <div className="flex-1 overflow-y-auto p-4 space-y-6 min-w-[20rem]">
                        {course.modules.map((module) => {
                            const isExpanded = expandedModules.includes(module.id);
                            const moduleTitle = module.id === lesson.moduleId ? (lesson.moduleTitle ?? module.title) : module.title;
                            return (
                                <div key={module.id} className="mb-6">
                                    <button
                                        type="button"
                                        onClick={() => toggleModule(module.id)}
                                        className="w-full flex items-center justify-between gap-2 mb-2 text-left group"
                                        aria-expanded={isExpanded}
                                    >
                                        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider group-hover:text-gray-700 transition-colors">{moduleTitle}</h4>
                                        <div className="flex items-center gap-1.5 flex-shrink-0">
                                            {!isExpanded && !module.isLocked && (module.earnedPoints ?? 0) > 0 && (
                                                <span className="text-[10px] font-bold text-amber-600">{module.earnedPoints} pts</span>
                                            )}
                                            <ChevronRight className={`w-3.5 h-3.5 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                                        </div>
                                    </button>
                                    {isExpanded && (
                                        <div className="space-y-1">
                                            {module.lessons.map((l, lessonIdx) => {
                                                const isCompleted = l.completed;
                                                return (
                                                    <SidebarItem
                                                        key={`${module.id}-${l.id || `pending-${lessonIdx}`}`}
                                                        title={l.title}
                                                        status={l.id === lesson.id ? (isCompleted ? 'active-completed' : 'active') : (isCompleted ? 'completed' : (module.isLocked || l.isLocked ? 'locked' : 'normal'))}
                                                        type={l.type ?? "video"}
                                                        earnedPoints={l.earnedPoints}
                                                        onClick={() => {
                                                            if (l.hasContent) {
                                                                navigate(`/courses/${courseParam}/lessons/${l.id}`);
                                                            }
                                                            if (window.innerWidth < 1024) {
                                                                setIsSidebarOpen(false);
                                                            }
                                                        }}
                                                    />
                                                )
                                            })}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    <div className="p-4 border-t border-gray-200 bg-white min-w-[20rem]">
                        <div className="flex justify-between items-center text-xs text-gray-500 mb-1">
                            <span>Course Progress</span>
                            <span className="font-bold text-slate-700">{course.progress}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-1.5 mb-2.5">
                            <div className="bg-indigo-600 h-1.5 rounded-full transition-all" style={{ width: `${course.progress}%` }}></div>
                        </div>
                        <div className="flex justify-between items-center text-xs text-slate-500 pt-1">
                            <span className="flex items-center gap-1 font-medium"><Sparkles className="w-3.5 h-3.5 text-amber-500" /> Total Points</span>
                            <span className="font-bold text-amber-600 bg-amber-50 px-2 py-0.5 rounded border border-amber-200/60">{totalEarnedPoints} pts</span>
                        </div>
                    </div>
                </aside>

                {/* Course Outline Drawer (Mobile & Tablet < 1024px) */}
                {isSidebarOpen && (
                    <div className="lg:hidden fixed inset-0 z-50 flex">
                        <div
                            className="fixed inset-0 bg-black/40 backdrop-blur-xs transition-opacity"
                            onClick={() => setIsSidebarOpen(false)}
                            aria-hidden="true"
                        />
                        <div className="relative ml-auto w-full max-w-xs sm:max-w-sm bg-gray-50 h-full shadow-2xl flex flex-col z-10">
                            <div className="p-3 border-b border-gray-200 flex items-center justify-between">
                                <div className="flex-1 min-w-0">
                                    <Link
                                        to={`/courses/${courseParam}`}
                                        onClick={() => setIsSidebarOpen(false)}
                                        className="text-xs text-gray-500 hover:text-indigo-600 mb-1.5 block flex items-center gap-1"
                                    >
                                        <ArrowLeft className="w-3 h-3" /> Back to Course Overview
                                    </Link>
                                    <div className="flex items-center justify-between gap-2">
                                        <h3 className="font-bold text-gray-900 leading-tight truncate min-w-0">{course.title}</h3>
                                        {course.level && course.level !== "Not determined yet" && (
                                            <span className="text-[10px] font-bold text-indigo-600 uppercase tracking-widest bg-indigo-50 px-1.5 py-0.5 rounded border border-indigo-100 flex-shrink-0">
                                                {course.level}
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <button
                                    onClick={() => setIsSidebarOpen(false)}
                                    className="ml-2 p-2 text-gray-400 hover:text-gray-900 transition-colors"
                                    aria-label="Close course outline"
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                            <div className="flex-1 overflow-y-auto p-4 space-y-6">
                                {course.modules.map((module) => {
                                    const isExpanded = expandedModules.includes(module.id);
                                    const moduleTitle = module.id === lesson.moduleId ? (lesson.moduleTitle ?? module.title) : module.title;
                                    return (
                                        <div key={module.id} className="mb-6">
                                            <button
                                                type="button"
                                                onClick={() => toggleModule(module.id)}
                                                className="w-full flex items-center justify-between gap-2 mb-2 text-left group"
                                                aria-expanded={isExpanded}
                                            >
                                                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider group-hover:text-gray-700 transition-colors">{moduleTitle}</h4>
                                                <div className="flex items-center gap-1.5 flex-shrink-0">
                                                    {!isExpanded && !module.isLocked && (module.earnedPoints ?? 0) > 0 && (
                                                        <span className="text-[10px] font-bold text-amber-600">{module.earnedPoints} pts</span>
                                                    )}
                                                    <ChevronRight className={`w-3.5 h-3.5 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                                                </div>
                                            </button>
                                            {isExpanded && (
                                                <div className="space-y-1">
                                                    {module.lessons.map((l, lessonIdx) => {
                                                        const isCompleted = l.completed;
                                                        return (
                                                            <SidebarItem
                                                                key={`${module.id}-${l.id || `pending-${lessonIdx}`}`}
                                                                title={l.title}
                                                                status={l.id === lesson.id ? (isCompleted ? 'active-completed' : 'active') : (isCompleted ? 'completed' : (module.isLocked || l.isLocked ? 'locked' : 'normal'))}
                                                                type={l.type ?? "video"}
                                                                earnedPoints={l.earnedPoints}
                                                                onClick={() => {
                                                                    if (l.hasContent) {
                                                                        navigate(`/courses/${courseParam}/lessons/${l.id}`);
                                                                    }
                                                                    setIsSidebarOpen(false);
                                                                }}
                                                            />
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                            <div className="p-4 border-t border-gray-200 bg-white">
                                <div className="flex justify-between items-center text-xs text-gray-500 mb-1">
                                    <span>Course Progress</span>
                                    <span className="font-bold text-slate-700">{course.progress}%</span>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-1.5 mb-2.5">
                                    <div className="bg-indigo-600 h-1.5 rounded-full transition-all" style={{ width: `${course.progress}%` }}></div>
                                </div>
                                <div className="flex justify-between items-center text-xs text-slate-500 pt-1">
                                    <span className="flex items-center gap-1 font-medium"><Sparkles className="w-3.5 h-3.5 text-amber-500" /> Total Points</span>
                                    <span className="font-bold text-amber-600 bg-amber-50 px-2 py-0.5 rounded border border-amber-200/60">{totalEarnedPoints} pts</span>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                <ResourceDrawer
                    courseId={course.id}
                    isOpen={isResourceDrawerOpen}
                    onClose={() => setIsResourceDrawerOpen(false)}
                    initialLessonId={lesson.canonicalLessonId}
                    lessonName={lesson.title}
                />
                <ResourceViewerModal
                    resource={viewingResource}
                    isOpen={viewingResource !== null}
                    onClose={() => setViewingResource(null)}
                />
            </div >
        </Tooltip.Provider >
    );
}

function SidebarItem({ title, status, type, earnedPoints, onClick }: { title: string, status: 'completed' | 'active' | 'active-completed' | 'locked' | 'normal', type: string, earnedPoints?: number, onClick: () => void }) {
    let icon = <div className="w-4 h-4 rounded-full border-2 border-gray-300" />;
    let style = "text-gray-500 hover:bg-gray-100";

    if (status === 'completed') {
        icon = <CheckCircle className="w-4 h-4 text-green-500" />;
        style = "text-gray-700 bg-gray-50 hover:bg-gray-100";
    } else if (status === 'active') {
        icon = <PlayCircle className="w-4 h-4 text-indigo-600" />;
        style = "text-indigo-600 font-medium bg-indigo-50 border-l-2 border-indigo-600";
    } else if (status === 'active-completed') {
        icon = <CheckCircle className="w-4 h-4 text-green-600" />;
        style = "text-indigo-700 font-medium bg-indigo-50 border-l-2 border-indigo-600";
    } else if (status === 'locked') {
        icon = <Lock className="w-4 h-4 text-gray-300" />;
        style = "text-gray-400 cursor-not-allowed";
    }

    if (type === 'quiz') {
        icon = <div className="w-4 h-4 flex items-center justify-center text-[10px] font-bold border border-current rounded bg-current/10">Q</div>;
        // Override quiz icon if completed
        if (status === 'completed' || status === 'active-completed') {
            icon = <CheckCircle className="w-4 h-4 text-green-500" />;
            if (status === 'active-completed') icon = <CheckCircle className="w-4 h-4 text-green-600" />;
        }
    }

    return (
        <Tooltip.Root delayDuration={0}>
            <Tooltip.Trigger asChild>
                <div
                    onClick={status !== 'locked' ? onClick : undefined}
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm flex items-start gap-3 transition-colors ${style} cursor-pointer`}
                >
                    <div className="flex-shrink-0 mt-0.5">{icon}</div>
                    <span className="flex-1 leading-snug">{title}</span>
                    {(earnedPoints ?? 0) > 0 && (
                        <span className="flex-shrink-0 mt-0.5 text-[10px] font-bold text-amber-600">{earnedPoints} pts</span>
                    )}
                </div>
            </Tooltip.Trigger>
            <Tooltip.Portal>
                <Tooltip.Content
                    side="right"
                    sideOffset={5}
                    className="bg-gray-900 text-white text-xs px-3 py-1.5 rounded shadow-lg z-50 animate-in fade-in zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out data-[state=closed]:zoom-out-95 max-w-[200px] break-words"
                >
                    {title}
                    <Tooltip.Arrow className="fill-gray-900" />
                </Tooltip.Content>
            </Tooltip.Portal>
        </Tooltip.Root>
    )
}
